/**
 * **A01 · The count sheet** and **A02 · the bag tag and the seed receipt** (Stream A,
 * Paper.dc.html), laid out by the `count_papers` engine from the count room's own bag registry,
 * its registers, the bag's custody (the issue and the pickup) and the day's reconciliation (the
 * register's cash from the Square pull). Scored against the drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import { FestivalClock, bagTag, countSheet } from '@cider/domain/engines';
import { formatTime } from '@cider/ui';
import type { Bag, Register } from '../views/money/CrShared.js';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

interface Custody {
  events: {
    event: string;
    at: string;
    toCustodian: string | null;
    fromCustodian: string | null;
    registerId: string | null;
    cents: number | null;
    /** The hand on this event, by name (money-new); null when People could not say. */
    byName?: string | null;
  }[];
}
interface Recon {
  rows: { registerId: string; area: string | null; readerCents: number | null }[];
}

function useBag(year: string, code: string) {
  const bags = useLoad<{ bags: Bag[] }>(`/money/countroom/${year}/bags`);
  const regs = useLoad<{ registers: Register[] }>(`/money/countroom/${year}/registers`);
  const custody = useLoad<Custody>(`/money/countroom/custody/bag/${code}?year=${year}`);
  const bag = bags.data?.bags.find((b) => b.bagCode === code) ?? null;
  const last = (event: string) => custody.data?.events.filter((e) => e.event === event).at(-1);
  const issued = last('issued');
  const picked = last('picked_up');
  const registerId = picked?.registerId ?? issued?.registerId ?? bag?.currentRegisterId ?? null;
  const reg = regs.data?.registers.find((r) => r.id === registerId) ?? null;
  const at = picked ?? issued;
  const day = at ? FestivalClock.operatingDay(new Date(at.at)) : null;
  // The day's reconciliation names the register's area and holds its cash from the Square pull.
  const recon = useLoad<Recon>(day ? `/money/countroom/${year}/${day}/reconciliation` : null);
  const row = recon.data?.rows.find((r) => r.registerId === registerId) ?? null;
  const loads = day ? [bags, regs, custody, recon] : [bags, regs, custody];
  return {
    bag,
    reg,
    issued,
    picked,
    area: row?.area ?? null,
    readerCents: row?.readerCents ?? null,
    error: bags.error ?? regs.error ?? null,
    ready: loads.every((l) => l.data !== null || l.error !== null),
  };
}

const seedOf = (
  issued: Custody['events'][number] | undefined,
  reg: Register | null,
): number | null => issued?.cents ?? reg?.profile?.seedCents ?? null;

export function CountSheet({ year, code }: { year: string; code: string }): ReactNode {
  const b = useBag(year, code);
  usePrinted('count-sheet', b.ready && !!b.bag, { ref: code, year: Number(year) });
  if (b.error) return <p role="alert">{b.error}</p>;
  if (!b.ready) return <p>Reading the bag…</p>;
  if (!b.bag)
    return (
      <p role="alert">
        No bag {code} in {year}’s registry.
      </p>
    );
  const page = countSheet({
    bag: b.bag.bagCode,
    area: b.area,
    register: b.reg?.name ?? null,
    pickedUp: b.picked
      ? {
          day: FestivalClock.operatingDay(new Date(b.picked.at)),
          time: formatTime(b.picked.at),
          // The runner, by name, from the custody (money-new).
          by: b.picked.byName ?? null,
        }
      : null,
    squareCashCents: b.readerCents,
    seedCents: seedOf(b.issued, b.reg),
  });
  return <DrawnPages pages={[page]} stamp="time" />;
}

export function BagTag({ year, code }: { year: string; code: string }): ReactNode {
  const b = useBag(year, code);
  usePrinted('bag-tag', b.ready && !!b.bag, { ref: code, year: Number(year) });
  if (b.error) return <p role="alert">{b.error}</p>;
  if (!b.ready) return <p>Reading the bag…</p>;
  if (!b.bag)
    return (
      <p role="alert">
        No bag {code} in {year}’s registry.
      </p>
    );
  const pages = bagTag({
    bag: b.bag.bagCode,
    area: b.area,
    register: b.reg?.name ?? null,
    seedCents: seedOf(b.issued, b.reg),
    issued: b.issued
      ? {
          day: FestivalClock.operatingDay(new Date(b.issued.at)),
          at: formatTime(b.issued.at),
          // Who issued it, by name, from the custody (money-new).
          by: b.issued.byName ?? null,
          to: b.issued.toCustodian,
        }
      : null,
  });
  return <DrawnPages pages={pages} stamp="time" />;
}
